import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { FieldMatcher } from './dataLists/FieldMatcher';
import { DataListColumn, DataListWebhookFieldMapping } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export interface BulkContactTargetField {
  id: string;
  name: string;
}

interface BulkContactMapperScreenProps {
  visible: boolean;
  onClose: () => void;
  headers: string[];
  totalRows: number;
  targetFields: BulkContactTargetField[];
  suggestedMapping: DataListWebhookFieldMapping[];
  fromRememberedMapping: boolean;
  committing: boolean;
  onConfirm: (mapping: DataListWebhookFieldMapping[]) => void;
}

/**
 * Same mapper as the Data List webhook contacts mapper — one card per
 * ENTITY FIELD (Name, Phone, City, ...), each picking which FILE column
 * feeds it, with Field/Fixed/Expression modes. Literally reuses
 * FieldMatcher.tsx (the webhook mapper's own component) rather than a
 * bespoke bulk-upload component, since the mapping direction and shape
 * (DataListWebhookFieldMapping: columnId/sourcePath/mode/fixedValue/
 * expression) are now identical between the two flows — targetColumns here
 * are synthesized BulkContactTargetField entries in DataListColumn's shape
 * (id/name/type only, since FieldMatcher never reads anything else off a
 * column that isn't a repeater). Any file column no mapping entry's
 * sourcePath points at (Field mode) becomes a custom field automatically —
 * resolved server-side (diffUnmappedHeaders), not shown as its own row
 * here.
 */
export const BulkContactMapperScreen: React.FC<BulkContactMapperScreenProps> = ({
  visible,
  onClose,
  headers,
  totalRows,
  targetFields,
  suggestedMapping,
  fromRememberedMapping,
  committing,
  onConfirm,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [mapping, setMapping] = useState<DataListWebhookFieldMapping[]>(suggestedMapping);

  React.useEffect(() => {
    setMapping(suggestedMapping);
  }, [suggestedMapping]);

  const targetColumns: DataListColumn[] = targetFields.map((f, idx) => ({
    id: f.id,
    name: f.name,
    type: 'text',
    nullable: true,
    order: idx,
    createdAt: '',
  }));

  const isMapped = (columnId: string) => {
    const m = mapping.find((x) => x.columnId === columnId);
    if (!m) return false;
    if (m.mode === 'fixed') return !!m.fixedValue;
    if (m.mode === 'expression') return !!m.expression;
    return !!m.sourcePath;
  };
  const hasName = isMapped('name');
  const hasIdentifier = ['phone', 'email', 'whatsapp', 'facebook-messenger', 'instagram', 'telegram'].some(isMapped);
  const canConfirm = hasName && hasIdentifier && !committing;

  const unmappedCount = headers.filter((h) => !mapping.some((m) => (m.mode || 'field') === 'field' && m.sourcePath === h)).length;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('contactsBulkUpload.mapper.title')}</Text>
              <Text style={[styles.subtitle, { textAlign: rtl.textStart }]}>
                {t('contactsBulkUpload.mapper.subtitle', { count: totalRows })}
              </Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>

          {fromRememberedMapping && (
            <View style={[styles.rememberedBanner, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="bookmark" size={14} color={colors.primary} />
              <Text style={[styles.rememberedBannerText, { textAlign: rtl.textStart }]}>{t('contactsBulkUpload.mapper.rememberedBanner')}</Text>
            </View>
          )}

          <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
            <FieldMatcher
              sourceFields={headers}
              targetColumns={targetColumns}
              value={mapping}
              onChange={setMapping}
              showExternalId={false}
            />
            {unmappedCount > 0 && (
              <View style={[styles.unmappedBanner, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="information-circle-outline" size={16} color={colors.text.secondary} />
                <Text style={[styles.unmappedBannerText, { textAlign: rtl.textStart }]}>
                  {t('contactsBulkUpload.mapper.unmappedBecomeCustom', { count: unmappedCount })}
                </Text>
              </View>
            )}
          </ScrollView>

          {!canConfirm && !committing && (
            <Text style={[styles.validationHint, { textAlign: rtl.textStart }]}>
              {!hasName ? t('contactsBulkUpload.mapper.needsName') : t('contactsBulkUpload.mapper.needsIdentifier')}
            </Text>
          )}

          <TouchableOpacity
            style={[styles.confirmButton, !canConfirm && styles.confirmButtonDisabled]}
            onPress={() => onConfirm(mapping)}
            disabled={!canConfirm}
          >
            {committing ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Text style={styles.confirmButtonText}>{t('contactsBulkUpload.mapper.confirm', { count: totalRows })}</Text>
            )}
          </TouchableOpacity>

          {committing && (
            // Large files import in the background and can take a few
            // minutes — say so, otherwise a long bare spinner reads as a
            // hang rather than genuine progress.
            <Text style={[styles.validationHint, { textAlign: 'center' }]}>
              {t('contactsBulkUpload.mapper.processingInBackground')}
            </Text>
          )}
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
    ...(Platform.OS === 'web' && { alignItems: 'center' as const }),
  },
  sheet: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '85%',
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
  },
  header: {
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: 2,
  },
  rememberedBanner: {
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: withAlpha(colors.primary, 0.06),
    borderRadius: designTokens.borderRadius.sm,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  rememberedBannerText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.primary,
  },
  list: {
    flexGrow: 0,
  },
  unmappedBanner: {
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.background.secondary,
    borderRadius: designTokens.borderRadius.sm,
    padding: spacing.sm,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  unmappedBannerText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  validationHint: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  confirmButton: {
    marginTop: spacing.sm,
    backgroundColor: colors.primary as string,
    borderRadius: designTokens.borderRadius.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  confirmButtonDisabled: {
    opacity: 0.5,
  },
  confirmButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },
});
